import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Search, SlidersHorizontal, ChevronDown, X, Bookmark } from 'lucide-react';
import { cn } from '@/lib/utils';

// ---------------------------------------------------------------------------
// Type Exports
// ---------------------------------------------------------------------------

export interface QuickFilterConfig {
  key: string;
  label: string;
  type: 'multi-select' | 'date-range' | 'user-select' | 'number-range';
  options?: { value: string; label: string; color?: string }[];
  icon?: React.ReactNode;
}

export interface ActiveFilter {
  key: string;
  label: string;
  value: any;
  displayValue: string;
}

export interface SavedView {
  id: string;
  name: string;
  filters: ActiveFilter[];
  isDefault?: boolean;
  isShared?: boolean;
}

export interface FilterBarProps {
  /** Quick filters config (3-4 max) */
  quickFilters: QuickFilterConfig[];
  /** Search placeholder */
  searchPlaceholder?: string;
  /** Current active filters */
  activeFilters: ActiveFilter[];
  /** Callbacks */
  onFilterChange: (filters: ActiveFilter[]) => void;
  onSearchChange: (term: string) => void;
  onMoreFiltersClick: () => void;
  /** Optional saved views */
  savedViews?: SavedView[];
  currentView?: string;
  onViewChange?: (viewId: string) => void;
  /** Search term */
  searchTerm?: string;
}

// ---------------------------------------------------------------------------
// Internal Sub-Components
// ---------------------------------------------------------------------------

/** Dropdown wrapper that handles click-outside closing */
const Dropdown: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
  className?: string;
}> = ({ isOpen, onClose, children, className }) => {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div
      ref={ref}
      className={cn(
        'absolute top-full left-0 z-50 mt-1 min-w-[220px] rounded-lg border border-gray-200 bg-white shadow-lg',
        className
      )}
    >
      {children}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Saved Views Dropdown
// ---------------------------------------------------------------------------

const SavedViewsDropdown: React.FC<{
  views: SavedView[];
  currentView?: string;
  onSelect: (viewId: string) => void;
}> = ({ views, currentView, onSelect }) => {
  const [open, setOpen] = useState(false);

  const activeView = views.find((v) => v.id === currentView);

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        className={cn(
          'flex items-center gap-1.5 rounded-lg border px-3 py-2 text-sm font-medium transition-colors',
          currentView
            ? 'border-blue-300 bg-blue-50 text-blue-700'
            : 'border-gray-200 bg-white text-gray-700 hover:border-blue-400'
        )}
      >
        <Bookmark className="h-3.5 w-3.5" />
        <span className="max-w-[120px] truncate">
          {activeView ? activeView.name : 'Saved Views'}
        </span>
        <ChevronDown className="h-3.5 w-3.5 opacity-60" />
      </button>

      <Dropdown isOpen={open} onClose={() => setOpen(false)}>
        <div className="py-1">
          {views.length === 0 && (
            <div className="px-3 py-2 text-xs text-gray-400">No saved views</div>
          )}
          {views.map((view) => (
            <button
              key={view.id}
              type="button"
              onClick={() => {
                onSelect(view.id);
                setOpen(false);
              }}
              className={cn(
                'flex w-full items-center gap-2 px-3 py-2 text-left text-sm transition-colors hover:bg-gray-50',
                view.id === currentView && 'bg-blue-50 text-blue-700'
              )}
            >
              <span className="flex-1 truncate">{view.name}</span>
              {view.isDefault && (
                <span className="rounded bg-gray-100 px-1.5 py-0.5 text-[10px] font-medium text-gray-500">
                  Default
                </span>
              )}
              {view.isShared && (
                <span className="rounded bg-blue-100 px-1.5 py-0.5 text-[10px] font-medium text-blue-600">
                  Shared
                </span>
              )}
            </button>
          ))}
        </div>
      </Dropdown>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Quick Filter Dropdown (multi-select & user-select)
// ---------------------------------------------------------------------------

const QuickFilterDropdown: React.FC<{
  config: QuickFilterConfig;
  activeFilters: ActiveFilter[];
  onToggleOption: (key: string, label: string, optionValue: string, optionLabel: string) => void;
  onSetRange: (key: string, label: string, value: { from: string; to: string }, displayValue: string) => void;
  onSetNumberRange: (key: string, label: string, value: { min?: number; max?: number }, displayValue: string) => void;
}> = ({ config, activeFilters, onToggleOption, onSetRange, onSetNumberRange }) => {
  const [open, setOpen] = useState(false);

  // Local state for date-range and number-range pickers
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [numMin, setNumMin] = useState('');
  const [numMax, setNumMax] = useState('');

  const selectedValues: string[] = activeFilters
    .filter((f) => f.key === config.key)
    .map((f) => (typeof f.value === 'string' ? f.value : ''));

  const isActive = activeFilters.some((f) => f.key === config.key);

  const handleApplyDateRange = () => {
    if (!dateFrom && !dateTo) return;
    const display = `${dateFrom || '...'} - ${dateTo || '...'}`;
    onSetRange(config.key, config.label, { from: dateFrom, to: dateTo }, display);
    setOpen(false);
  };

  const handleApplyNumberRange = () => {
    const min = numMin ? parseFloat(numMin) : undefined;
    const max = numMax ? parseFloat(numMax) : undefined;
    if (min === undefined && max === undefined) return;
    const display = `${min ?? 0} - ${max ?? 'Any'}`;
    onSetNumberRange(config.key, config.label, { min, max }, display);
    setOpen(false);
  };

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        className={cn(
          'flex items-center gap-1.5 rounded-lg border px-3 py-2 text-sm font-medium transition-colors whitespace-nowrap',
          isActive
            ? 'border-blue-300 bg-blue-50 text-blue-700'
            : 'border-gray-200 bg-white text-gray-700 hover:border-blue-400'
        )}
      >
        {config.icon && <span className="flex-shrink-0">{config.icon}</span>}
        <span>{config.label}</span>
        {isActive && (
          <span className="flex h-4 min-w-[16px] items-center justify-center rounded-full bg-blue-600 px-1 text-[10px] font-bold text-white">
            {activeFilters.filter((f) => f.key === config.key).length}
          </span>
        )}
        <ChevronDown className="h-3.5 w-3.5 opacity-60" />
      </button>

      <Dropdown isOpen={open} onClose={() => setOpen(false)} className="max-h-[320px]">
        {/* Multi-select / User-select */}
        {(config.type === 'multi-select' || config.type === 'user-select') && config.options && (
          <div className="overflow-y-auto py-1" style={{ maxHeight: 280 }}>
            {config.options.map((opt) => {
              const checked = selectedValues.includes(opt.value);
              return (
                <label
                  key={opt.value}
                  className="flex cursor-pointer items-center gap-2 px-3 py-2 text-sm hover:bg-gray-50"
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() =>
                      onToggleOption(config.key, config.label, opt.value, opt.label)
                    }
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  {opt.color && (
                    <span
                      className="h-2.5 w-2.5 flex-shrink-0 rounded-full"
                      style={{ backgroundColor: opt.color }}
                    />
                  )}
                  <span className="text-gray-700">{opt.label}</span>
                </label>
              );
            })}
          </div>
        )}

        {/* Date range */}
        {config.type === 'date-range' && (
          <div className="space-y-3 p-3">
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-500">From</label>
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className="w-full rounded-md border border-gray-300 px-2.5 py-1.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-500">To</label>
              <input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className="w-full rounded-md border border-gray-300 px-2.5 py-1.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </div>
            <button
              type="button"
              onClick={handleApplyDateRange}
              className="w-full rounded-md bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700 transition-colors"
            >
              Apply
            </button>
          </div>
        )}

        {/* Number range */}
        {config.type === 'number-range' && (
          <div className="space-y-3 p-3">
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-500">Min</label>
              <input
                type="number"
                value={numMin}
                onChange={(e) => setNumMin(e.target.value)}
                placeholder="0"
                className="w-full rounded-md border border-gray-300 px-2.5 py-1.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-500">Max</label>
              <input
                type="number"
                value={numMax}
                onChange={(e) => setNumMax(e.target.value)}
                placeholder="Any"
                className="w-full rounded-md border border-gray-300 px-2.5 py-1.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </div>
            <button
              type="button"
              onClick={handleApplyNumberRange}
              className="w-full rounded-md bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700 transition-colors"
            >
              Apply
            </button>
          </div>
        )}
      </Dropdown>
    </div>
  );
};

// ---------------------------------------------------------------------------
// FilterBar (Main Component)
// ---------------------------------------------------------------------------

const FilterBar: React.FC<FilterBarProps> = ({
  quickFilters,
  searchPlaceholder = 'Search...',
  activeFilters,
  onFilterChange,
  onSearchChange,
  onMoreFiltersClick,
  savedViews,
  currentView,
  onViewChange,
  searchTerm = '',
}) => {
  const [localSearch, setLocalSearch] = useState(searchTerm);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Sync external search term changes
  useEffect(() => {
    setLocalSearch(searchTerm);
  }, [searchTerm]);

  // Debounced search (300ms)
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      onSearchChange(localSearch);
    }, 300);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localSearch]);

  // ----- filter helpers -----

  /** Toggle a single value within a multi-select / user-select quick filter */
  const handleToggleOption = useCallback(
    (key: string, filterLabel: string, optionValue: string, optionLabel: string) => {
      const exists = activeFilters.find(
        (f) => f.key === key && f.value === optionValue
      );
      if (exists) {
        onFilterChange(activeFilters.filter((f) => !(f.key === key && f.value === optionValue)));
      } else {
        onFilterChange([
          ...activeFilters,
          { key, label: filterLabel, value: optionValue, displayValue: optionLabel },
        ]);
      }
    },
    [activeFilters, onFilterChange]
  );

  /** Set a date-range filter (replaces any existing range for that key) */
  const handleSetRange = useCallback(
    (key: string, label: string, value: { from: string; to: string }, displayValue: string) => {
      const without = activeFilters.filter((f) => f.key !== key || typeof f.value === 'string');
      // For date-range, we store a single entry with an object value
      onFilterChange([
        ...without.filter((f) => f.key !== key),
        { key, label, value, displayValue },
      ]);
    },
    [activeFilters, onFilterChange]
  );

  /** Set a number-range filter */
  const handleSetNumberRange = useCallback(
    (key: string, label: string, value: { min?: number; max?: number }, displayValue: string) => {
      onFilterChange([
        ...activeFilters.filter((f) => f.key !== key),
        { key, label, value, displayValue },
      ]);
    },
    [activeFilters, onFilterChange]
  );

  /** Remove a single active filter chip */
  const handleRemoveFilter = useCallback(
    (filter: ActiveFilter) => {
      onFilterChange(
        activeFilters.filter(
          (f) => !(f.key === filter.key && f.value === filter.value)
        )
      );
    },
    [activeFilters, onFilterChange]
  );

  /** Clear all active filters */
  const handleClearAll = useCallback(() => {
    onFilterChange([]);
  }, [onFilterChange]);

  // Count of filters surfaced in "More Filters" (those not matching any quick filter key)
  const moreFilterKeys = new Set(quickFilters.map((q) => q.key));
  const moreFiltersCount = activeFilters.filter((f) => !moreFilterKeys.has(f.key)).length;
  const totalFilterCount = activeFilters.length;

  return (
    <div className="space-y-2">
      {/* Main bar */}
      <div className="flex items-center gap-2 h-12">
        {/* Saved Views */}
        {savedViews && savedViews.length > 0 && onViewChange && (
          <SavedViewsDropdown
            views={savedViews}
            currentView={currentView}
            onSelect={onViewChange}
          />
        )}

        {/* Quick Filters */}
        {quickFilters.map((qf) => (
          <QuickFilterDropdown
            key={qf.key}
            config={qf}
            activeFilters={activeFilters}
            onToggleOption={handleToggleOption}
            onSetRange={handleSetRange}
            onSetNumberRange={handleSetNumberRange}
          />
        ))}

        {/* Search */}
        <div className="relative ml-auto flex items-center">
          <Search className="absolute left-2.5 h-4 w-4 text-gray-400 pointer-events-none" />
          <input
            type="text"
            value={localSearch}
            onChange={(e) => setLocalSearch(e.target.value)}
            placeholder={searchPlaceholder}
            className="w-48 rounded-lg border border-gray-200 bg-white py-2 pl-8 pr-8 text-sm text-gray-700 placeholder-gray-400 transition-all focus:w-64 focus:border-blue-400 focus:outline-none focus:ring-1 focus:ring-blue-400"
          />
          {localSearch && (
            <button
              type="button"
              onClick={() => {
                setLocalSearch('');
                onSearchChange('');
              }}
              className="absolute right-2 rounded p-0.5 text-gray-400 hover:text-gray-600"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>

        {/* More Filters */}
        <button
          type="button"
          onClick={onMoreFiltersClick}
          className="relative flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-medium text-gray-600 transition-colors hover:text-blue-600"
        >
          <SlidersHorizontal className="h-4 w-4" />
          <span>More Filters</span>
          {moreFiltersCount > 0 && (
            <span className="flex h-4 min-w-[16px] items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold text-white">
              {moreFiltersCount}
            </span>
          )}
        </button>
      </div>

      {/* Active filter chips row */}
      {totalFilterCount > 0 && (
        <div className="flex flex-wrap items-center gap-1.5">
          {activeFilters.map((filter, idx) => (
            <span
              key={`${filter.key}-${idx}`}
              className="inline-flex items-center gap-1 rounded-full border border-blue-200 bg-blue-50 px-2.5 py-1 text-xs font-medium text-blue-700"
            >
              <span className="text-blue-500">{filter.label}:</span>
              <span>{filter.displayValue}</span>
              <button
                type="button"
                onClick={() => handleRemoveFilter(filter)}
                className="ml-0.5 rounded-full p-0.5 transition-colors hover:bg-blue-200"
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          ))}
          <button
            type="button"
            onClick={handleClearAll}
            className="ml-1 text-xs text-gray-500 underline hover:text-gray-700"
          >
            Clear all
          </button>
        </div>
      )}
    </div>
  );
};

export default FilterBar;
